import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import { loginRateLimiter, passwordResetRateLimiter } from '../utils/rateLimiter';
import { encryptionUtils } from '../utils/encryption';
import Button from '../ui/Button';
import Dialog from '../ui/Dialog/Dialog';
import { Field, Input } from '../ui/Field/Field';
import '../ui/Field/field.css';
import { dubaiHour } from '../utils/dubaiTime';

// The builder's links (the name on the sign-in page opens them)
const CONTACT = [
  { key: 'in', label: 'LinkedIn', sub: 'mohamed-alamir', href: 'https://www.linkedin.com/in/mohamed-alamir/', badge: 'in' },
  { key: 'cv', label: 'CV website', sub: 'moalamir52.github.io', href: 'https://moalamir52.github.io/', badge: 'CV' },
  { key: 'wa', label: 'WhatsApp', sub: '+971 55 379 6104', href: 'https://wa.me/971553796104', badge: 'WA' },
  { key: 'tel', label: 'Call', sub: '+971 55 379 6104', href: 'tel:+971553796104', badge: '\u260E' },
];

/** Dubai's day, date and time, kept current (the sign-in side's small live touch). */
function DubaiClock() {
  const [now, setNow] = useState(() => new Date());
  React.useEffect(() => { const t = setInterval(() => setNow(new Date()), 20000); return () => clearInterval(t); }, []);
  const opt = { timeZone: 'Asia/Dubai' };
  const day = now.toLocaleDateString('en-GB', { ...opt, weekday: 'long', day: 'numeric', month: 'long' });
  const time = now.toLocaleTimeString('en-GB', { ...opt, hour: '2-digit', minute: '2-digit' });
  return (
    <p className="login__clock" aria-label={`Dubai, ${day}, ${time}`}>
      <span className="login__clock-dot" aria-hidden="true" />
      Dubai · {day} · <b>{time}</b>
    </p>
  );
}

/** "Mohamed Elamir" -> a small glass menu with LinkedIn, the CV site, WhatsApp and a call. */
function CreditMenu() {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open]);
  return (
    <div className="login__credit-menu" ref={ref}>
      <button type="button" className="login__credit-name" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Mohamed Elamir <span aria-hidden="true" className="login__caret">&#9662;</span>
      </button>
      {open && (
        <div className="login__contact" role="menu" aria-label="Contact Mohamed Elamir">
          {CONTACT.map((c) => (
            <a key={c.key} role="menuitem" className={`login__contact-item is-${c.key}`} href={c.href}
              target={c.key === 'tel' ? undefined : '_blank'} rel="noopener noreferrer" onClick={() => setOpen(false)}>
              <span className="login__contact-badge" aria-hidden="true">{c.badge}</span>
              <span className="login__contact-text"><b>{c.label}</b><span>{c.sub}</span></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, currentUser, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot Password State
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  React.useEffect(() => {
    document.title = 'Sign in · Yelo Dubai';
  }, []);

  React.useEffect(() => {
    if (currentUser) {
      navigate('/');      // the first page the role opens (HomeRedirect)
    }
  }, [currentUser, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // Rate limiting check
    const clientIP = 'user-' + (email || 'unknown'); // In production, use real IP
    const rateLimitCheck = loginRateLimiter.isRateLimited(clientIP);

    if (rateLimitCheck.limited) {
      setError(`Too many failed attempts. Try again in ${rateLimitCheck.resetTime} seconds.`);
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);

      // Reset rate limit on successful login
      loginRateLimiter.resetAttempts(clientIP);
    } catch (err) {
      // Record failed attempt
      loginRateLimiter.recordAttempt(clientIP);

      const emailHash = encryptionUtils.hashForLogs(email);
      logger.error(`[Login] Auth Failed for: ${emailHash}`, err.code || 'unknown_error');

      const remaining = loginRateLimiter.getRemainingAttempts(clientIP);

      if (err.message.includes('invalid-credential')) {
        setError(`Invalid email or password. ${remaining} attempts remaining.`);
      } else {
        setError('Sign-in failed: ' + err.message);
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    // Rate limiting for password reset
    const clientIP = 'reset-' + (forgotEmail || 'unknown');
    const rateLimitCheck = passwordResetRateLimiter.isRateLimited(clientIP);

    if (rateLimitCheck.limited) {
      setForgotError(`Too many reset attempts. Try again in ${Math.ceil(rateLimitCheck.resetTime / 60)} minutes.`);
      return;
    }

    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);

    try {
      await resetPassword(forgotEmail);
      setForgotMessage('Sent. Check your inbox - and the Spam / Junk folder: the email comes from noreply@yelo-dubai-management.firebaseapp.com.');
      passwordResetRateLimiter.resetAttempts(clientIP);
    } catch (err) {
      passwordResetRateLimiter.recordAttempt(clientIP);
      const emailHash = encryptionUtils.hashForLogs(forgotEmail);
      logger.error(`[Login] Reset Password Failed for: ${emailHash}`, err.code);
      setForgotError('Could not send the reset link: ' + err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const hour = dubaiHour();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const year = new Date().getFullYear();

  return (
    <div className="login">
      {/* the brand side: who we are, what this is, who made it */}
      <aside className="login__hero" aria-label="About Yelo Dubai">
        {/* the sun: rising behind the logo, its light falling over the page */}
        <span className="login__sun" aria-hidden="true" />
        <span className="login__rays" aria-hidden="true" />
        <img className="login__logo" src="/assets/yelo-logo-yellow.png" alt="Yelo" />
        <div className="login__welcome">
          <p className="login__ar">Welcome to Yelo Dubai</p>
          <h2 className="login__headline">Everything Yelo Dubai runs on, in one place.</h2>
          <ul className="login__features">
            <li><span aria-hidden="true">📄</span>Contracts, bookings &amp; daily rentals</li>
            <li><span aria-hidden="true">🚗</span>Fleet, maintenance &amp; registrations</li>
            <li><span aria-hidden="true">🚦</span>Fines, Salik, Darb &amp; parking</li>
            <li><span aria-hidden="true">⚡</span>The Engine: Ejar, live, around the clock</li>
          </ul>
        </div>
        <div className="login__credit">
          <span className="login__credit-by">Designed &amp; built by</span>
          <CreditMenu />
          <span className="login__credit-copy">© 2024–{year} Mohamed Elamir. All rights reserved.</span>
        </div>
      </aside>

      <main className="login__side">
        <DubaiClock />
        <div className="login__panel">
          <div className="login__brand">
            <img className="login__mark" src="/assets/yelo-icon.png" alt="" aria-hidden="true" />
            <span>
              <span className="login__name">Yelo Dubai</span>
              <span className="login__sub">Fleet operations</span>
            </span>
          </div>

          <h1 className="login__title">{greet} <span aria-hidden="true">👋</span></h1>
          <p className="login__lead">Sign in with your company email to continue.</p>

          {error && (
            <div className="login__alert" role="alert">{error}</div>
          )}

          <form onSubmit={handleLogin} className="login__form" noValidate={false}>
            <Field label="Email" required>
              <Input
                type="email"
                size="lg"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@yelo.com"
                required
              />
            </Field>

            <Field
              label="Password"
              required
              labelAction={(
                <button
                  type="button"
                  // out of the Tab order (owner, 26 Sep 2026): Tab from the e-mail goes straight to the password
                  tabIndex={-1}
                  className="yelo-btn yelo-btn--link yelo-btn--xs"
                  onClick={() => { setShowForgotModal(true); setForgotEmail(email); setForgotMessage(''); setForgotError(''); }}
                >
                  Forgot password?
                </button>
              )}
            >
              <div className="login__pw">
                <Input
                  type={showPw ? 'text' : 'password'}
                  size="lg"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="button" className="login__eye" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide the password' : 'Show the password'} aria-pressed={showPw}>
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>

            <Button type="submit" variant="primary" size="lg" fullWidth isLoading={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="login__foot">
            Restricted access for authorised Yelo staff.<br />
            <span className="login__foot-copy">© 2024–{year} Mohamed Elamir · Yelo Dubai</span>
          </p>
        </div>
      </main>

      <Dialog
        open={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        title="Reset password"
        description="We will email you a link to choose a new password."
        size="sm"
        footer={(
          <>
            <Button onClick={() => setShowForgotModal(false)}>Cancel</Button>
            <Button type="submit" form="forgot-form" variant="primary" isLoading={forgotLoading}>
              Send reset link
            </Button>
          </>
        )}
      >
        <form id="forgot-form" onSubmit={handleForgotPassword} className="login__form">
          {forgotError && <div className="login__alert" role="alert">{forgotError}</div>}
          {forgotMessage && <div className="login__alert login__alert--ok" role="status">{forgotMessage}</div>}
          <Field label="Email" required>
            <Input
              type="email"
              autoComplete="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="name@yelo.com"
              required
            />
          </Field>
          <p className="login__hint">
            No email after 5 minutes? Look in <b>Spam / Junk</b>, or ask your admin: they can send you a
            reset link or a temporary password on WhatsApp from <b>Administration → Users</b>.
          </p>
        </form>
      </Dialog>

      <style>{`
        .login { min-height: 100vh; display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr); background: var(--bg-app); }
        .login__hero { position: relative; overflow: hidden; isolation: isolate; display: flex; flex-direction: column; align-items: center;
          justify-content: space-between; gap: var(--space-8); text-align: center;
          padding: var(--space-8) var(--space-8) var(--space-6); color: #fff;
          background: radial-gradient(120% 90% at 50% 0%, #6d1fa0 0%, #500B78 45%, #2c0645 100%); }
        /* the sun at the top, and the light it pours down the page */
        .login__hero::before { content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
          background: radial-gradient(70% 55% at 50% 0%, rgb(255 214 64 / 0.22) 0%, rgb(255 204 0 / 0.08) 45%, transparent 75%); }
        .login__sun { position: absolute; top: -300px; left: 50%; width: 520px; height: 520px; margin-left: -260px; z-index: -1;
          border-radius: 50%; pointer-events: none;
          background: radial-gradient(circle, rgb(255 236 150 / 0.95) 0%, rgb(255 214 64 / 0.75) 14%, rgb(255 204 0 / 0.30) 32%, rgb(255 204 0 / 0.08) 52%, transparent 70%);
          animation: login-glow 6s ease-in-out infinite alternate; }
        .login__rays { position: absolute; top: -600px; left: 50%; width: 1140px; height: 1140px; margin-left: -570px; z-index: -1;
          border-radius: 50%; pointer-events: none; opacity: 0.55;
          background: repeating-conic-gradient(from 0deg, rgb(255 214 64 / 0.16) 0deg 4deg, transparent 4deg 15deg);
          -webkit-mask-image: radial-gradient(circle, #000 18%, transparent 62%); mask-image: radial-gradient(circle, #000 18%, transparent 62%);
          animation: login-spin 90s linear infinite; }
        @keyframes login-spin { to { transform: rotate(360deg); } }
        @keyframes login-glow { from { opacity: 0.85; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .login__rays, .login__sun { animation: none; } }
        .login__logo { width: 150px; height: auto; margin-top: var(--space-6); filter: drop-shadow(0 3px 2px rgb(44 6 69 / 0.55)) drop-shadow(0 8px 22px rgb(44 6 69 / 0.45)); }
        .login__welcome { max-width: 460px; width: 100%; display: flex; flex-direction: column; align-items: center; }
        .login__ar { font-size: 28px; font-weight: 700; color: #FFCC00; margin-bottom: 6px; }
        .login__headline { font-size: 30px; line-height: 1.2; font-weight: 700; letter-spacing: -0.01em; }
        .login__features { list-style: none; margin: var(--space-6) 0 0; padding: 0; display: grid; gap: 10px; width: 100%; }
        .login__features li { display: flex; align-items: center; justify-content: center; gap: 12px; font-size: 15px; color: rgb(255 255 255 / 0.92);
          background: rgb(255 255 255 / 0.07); border: 1px solid rgb(255 255 255 / 0.12); border-radius: 12px; padding: 10px 14px;
          backdrop-filter: blur(2px); }
        .login__features li span { font-size: 18px; }
        .login__credit { display: flex; flex-direction: column; align-items: center; gap: 2px; width: 100%; border-top: 1px solid rgb(255 255 255 / 0.18); padding-top: var(--space-4); }
        .login__credit-by { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: rgb(255 255 255 / 0.65); }
        .login__credit-menu { position: relative; }
        .login__credit-name { font-size: 20px; font-weight: 700; color: #FFCC00; background: none; border: 0; cursor: pointer;
          padding: 2px 10px; border-radius: 10px; font-family: inherit; }
        .login__credit-name:hover, .login__credit-name[aria-expanded="true"] { background: rgb(255 204 0 / 0.12); }
        .login__credit-name:focus-visible { outline: 2px solid #FFCC00; outline-offset: 2px; }
        .login__caret { font-size: 13px; opacity: 0.8; }
        .login__contact { position: absolute; bottom: calc(100% + 10px); left: 50%; margin-left: -135px; width: 270px; z-index: 5;
          display: grid; gap: 4px; padding: 8px; text-align: left; border-radius: 14px;
          background: rgb(40 6 64 / 0.92); border: 1px solid rgb(255 204 0 / 0.35); backdrop-filter: blur(10px);
          box-shadow: 0 18px 40px rgb(0 0 0 / 0.45), 0 0 0 1px rgb(255 255 255 / 0.04) inset; animation: login-pop 140ms ease-out; }
        @keyframes login-pop { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .login__contact-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: 10px; color: #fff; text-decoration: none; }
        .login__contact-item:hover, .login__contact-item:focus-visible { background: rgb(255 255 255 / 0.09); outline: none; }
        .login__contact-badge { width: 32px; height: 32px; flex: none; display: grid; place-items: center; border-radius: 9px;
          font-size: 13px; font-weight: 800; color: #fff; }
        .login__contact-item.is-in .login__contact-badge { background: #0A66C2; }
        .login__contact-item.is-cv .login__contact-badge { background: #FFCC00; color: #500B78; }
        .login__contact-item.is-wa .login__contact-badge { background: #1FAF55; }
        .login__contact-item.is-tel .login__contact-badge { background: rgb(255 255 255 / 0.16); font-size: 15px; }
        .login__contact-text { display: flex; flex-direction: column; line-height: 1.2; }
        .login__contact-text b { font-size: 14px; }
        .login__contact-text span { font-size: 12px; color: rgb(255 255 255 / 0.7); }
        .login__contact-item.is-wa .login__contact-text span, .login__contact-item.is-tel .login__contact-text span { color: #6EE7A0; }
        .login__credit-copy { font-size: 12px; color: rgb(255 255 255 / 0.7); }
        .login__side { position: relative; overflow: hidden; isolation: isolate; display: flex; flex-direction: column; align-items: center;
          justify-content: center; gap: var(--space-4); padding: var(--space-6) var(--space-4); }
        /* a quiet background: faint dots, a soft purple glow and a touch of the sun's yellow */
        .login__side::before { content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
          background-image: radial-gradient(rgb(80 11 120 / 0.10) 1px, transparent 1.2px); background-size: 22px 22px;
          -webkit-mask-image: radial-gradient(70% 60% at 50% 45%, #000 20%, transparent 80%); mask-image: radial-gradient(70% 60% at 50% 45%, #000 20%, transparent 80%); }
        .login__side::after { content: ''; position: absolute; inset: 0; z-index: -2; pointer-events: none;
          background: radial-gradient(40% 35% at 85% 10%, rgb(109 31 160 / 0.14), transparent 70%),
                      radial-gradient(35% 30% at 10% 95%, rgb(255 204 0 / 0.16), transparent 70%); }
        .login__clock { display: inline-flex; align-items: center; gap: 8px; font-size: var(--text-xs); color: var(--text-2);
          background: var(--surface); border: 1px solid var(--border); border-radius: 999px; padding: 6px 12px;
          box-shadow: 0 2px 10px rgb(80 11 120 / 0.06); }
        .login__clock b { color: var(--text); font-variant-numeric: tabular-nums; }
        .login__clock-dot { width: 8px; height: 8px; border-radius: 50%; background: #FFCC00; box-shadow: 0 0 0 3px rgb(255 204 0 / 0.25); }
        .login__panel { position: relative; width: 100%; max-width: 400px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-xl);
          padding: var(--space-8) var(--space-6) var(--space-6); box-shadow: 0 10px 40px rgb(80 11 120 / 0.10); }
        .login__brand { display: flex; align-items: center; gap: 10px; margin-bottom: var(--space-6); }
        .login__brand > span:last-child { display: flex; flex-direction: column; line-height: 1.15; }
        .login__mark { width: 40px; height: 40px; border-radius: 10px; object-fit: cover; box-shadow: 0 2px 6px rgb(80 11 120 / 0.25); }
        .login__name { font-weight: 700; font-size: var(--text-md); }
        .login__sub { font-size: var(--text-xs); color: var(--text-3); }
        .login__title { font-size: 26px; font-weight: 700; letter-spacing: -0.01em; }
        .login__lead { margin-top: 4px; margin-bottom: var(--space-5); font-size: var(--text-sm); color: var(--text-3); }
        .login__form { display: flex; flex-direction: column; gap: var(--space-4); }
        .login__pw { position: relative; }
        .login__pw input { padding-right: 64px; }
        .login__eye { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); border: 0; background: none; cursor: pointer;
          font-size: var(--text-xs); font-weight: 600; color: var(--accent-text); padding: 4px 6px; border-radius: 6px; }
        .login__eye:hover { background: rgb(var(--accent-50)); }
        .login__alert { padding: 10px 12px; border-radius: var(--radius-md); background: var(--danger-soft); color: var(--danger); font-size: var(--text-sm); margin-bottom: var(--space-4); }
        .login__form .login__alert { margin-bottom: 0; }
        .login__alert--ok { background: var(--success-soft); color: var(--success); }
        .login__hint { font-size: var(--text-xs); color: var(--text-3); line-height: 1.5; }
        .login__foot { margin-top: var(--space-6); font-size: var(--text-xs); color: var(--text-3); text-align: center; }
        .login__foot-copy { color: var(--text-2); font-weight: 600; }
        @media (max-width: 900px) {
          .login { grid-template-columns: 1fr; }
          .login__hero { padding: var(--space-6) var(--space-4); gap: var(--space-4); }
          .login__logo { width: 96px; }
          .login__headline { font-size: 22px; }
          .login__ar { font-size: 22px; }
          .login__features { display: none; }
        }
      `}</style>
    </div>
  );
}
